/**
 * DemoCheckoutSuccess — Iter275.5
 *
 * Synthetic checkout landing page hit when Demo Mode is active. Mirrors
 * the real /audit/billing/success layout but with a candid "this was
 * simulated" message so prospects + beta testers know nothing was
 * actually charged.
 *
 * Entitlement on the account has ALREADY been flipped server-side by
 * `_apply_grant` inside `_start_demo_checkout`, so by the time the user
 * lands here the upgraded state is real (until the row is purged).
 */
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Sparkles, ArrowRight } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function DemoCheckoutSuccess() {
  useDocumentTitle("Demo Checkout Success — QorTrace");
  const [searchParams] = useSearchParams();
  const session_id = searchParams.get("session_id") || "";
  const plan_id = searchParams.get("plan_id") || "";
  const next = searchParams.get("next") || "/account/subscriptions";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--qt-bg)" }}>
      <Header />
      <main className="flex-1 max-w-[800px] mx-auto px-6 sm:px-8 py-16 w-full"
            data-testid="demo-checkout-success">
        <div className="qt-card"
             style={{
               border: "1px solid #FFB000",
               background: "linear-gradient(135deg, rgba(255,176,0,0.06), transparent 70%)",
               padding: "32px 36px",
             }}>
          <div className="flex items-center gap-3">
            <Sparkles size={24} style={{ color: "#FFB000" }} />
            <div className="qt-label" style={{ color: "#FFB000" }}>
              DEMO MODE · SIMULATED PURCHASE
            </div>
          </div>

          <h1 className="qt-h2 text-3xl mt-3" style={{ color: "#fff" }}>
            You're all set — for demo purposes.
          </h1>

          <p className="text-base mt-3" style={{ color: "var(--qt-text-2)" }}>
            No card was charged and no Stripe session was created. Your account
            entitlements have been flipped on so you can explore the full
            post-purchase experience as if it were real — dashboards, alerts,
            unlocked tabs, the whole thing.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            <div className="qt-card"
                 style={{ background: "rgba(7,10,18,0.6)", padding: "14px 16px" }}>
              <div className="qt-label">DEMO SESSION</div>
              <div className="qt-mono text-xs mt-1"
                   style={{ color: "var(--qt-text-2)", wordBreak: "break-all" }}>
                {session_id || "—"}
              </div>
            </div>
            <div className="qt-card"
                 style={{ background: "rgba(7,10,18,0.6)", padding: "14px 16px" }}>
              <div className="qt-label">PLAN UNLOCKED</div>
              <div className="qt-mono text-xs mt-1"
                   style={{ color: "var(--qt-text-2)" }}>
                {plan_id || "—"}
              </div>
            </div>
          </div>

          <div className="mt-7 flex items-center gap-3 flex-wrap">
            <Link to={next}
                  data-testid="demo-success-continue"
                  className="qt-btn qt-btn-primary inline-flex"
                  style={{ fontSize: 13 }}>
              <CheckCircle2 size={14} /> Continue exploring <ArrowRight size={14} />
            </Link>
            <Link to="/account/subscriptions"
                  className="qt-btn qt-btn-ghost inline-flex"
                  style={{ fontSize: 13, color: "var(--qt-text-2)" }}>
              View my subscriptions
            </Link>
          </div>

          <p className="text-xs mt-7"
             style={{ color: "var(--qt-text-3)", lineHeight: "1.65" }}>
            When Demo Mode is disabled, all rows flagged <code>demo: true</code> are
            visible to superadmin for one-click purging. Your normal access tier
            is restored automatically.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
